import React from 'react';
import { IconArrowRight } from '@tabler/icons-react';
import { Flex, useMantineTheme, Box, Group, Card } from '@mantine/core';

import RYPBadge from '~/domains/design-system/badges/RYPBadge/RYPBadge';
import OFPBadge from '~/domains/design-system/badges/OFPBadge/OFPBadge';
import SplashBadge from '~/domains/design-system/badges/SplashBadge/SplashBadge';

import useStyles from './WelcomeModal.styles';

export type WelcomeModalHeaderType = 'ryp' | 'ofp' | 'combo';

interface WelcomeModalHeaderProps {
  type?: WelcomeModalHeaderType;
  isCardsHidden?: boolean;
}

function WelcomeModalHeader({ type = 'ryp', isCardsHidden = false }: WelcomeModalHeaderProps) {
  const theme = useMantineTheme();
  const { classes } = useStyles({ isCardsHidden });

  const renderHeaderBadges = () => {
    switch (type) {
      case 'ryp':
        return (
          <>
            <RYPBadge width={60} height={60} />
            <Box className={classes.arrowContainer}>
              <IconArrowRight color={theme.black} size={16} />
            </Box>
            <SplashBadge iconColor={theme.black} width={60} height={60} />
          </>
        );

      case 'ofp':
        return (
          <>
            <OFPBadge width={60} height={60} />
            <Box className={classes.arrowContainer}>
              <IconArrowRight color={theme.black} size={16} />
            </Box>
            <SplashBadge iconColor={theme.black} width={60} height={60} />
          </>
        );

      case 'combo':
        return (
          <>
            <Box className={classes.comboBadgeContainer}>
              <Box className={classes.comboBadgeOFP}>
                <OFPBadge width={40} height={40} />
              </Box>
              <Box className={classes.comboBadgeRYP}>
                <RYPBadge width={40} height={40} />
              </Box>
            </Box>

            <Box className={classes.arrowContainer}>
              <IconArrowRight color={theme.black} size={16} />
            </Box>

            <SplashBadge iconColor={theme.black} width={60} height={60} />
          </>
        );

      default:
        return null;
    }
  };

  const getCardBadgeType = (cardIndex: number) => {
    switch (type) {
      case 'ryp':
        return 'ryp';
      case 'ofp':
        return 'ofp';
      case 'combo':
        return cardIndex === 1 ? 'ofp' : 'ryp';
      default:
        return null;
    }
  };

  const renderCardMockup = (badgeType: 'ryp' | 'ofp' | null) => (
    <Card p="xs" shadow="sm" radius="lg" className={classes.mockupCard}>
      <Group className={classes.mockupGroup}>
        <Group noWrap className={classes.mockupGroup}>
          <Box className={classes.mockupAvatarContainer}>
            <Box className={classes.mockupAvatar} />
            {badgeType && (
              <Box className={classes.mockupBadge}>
                {badgeType === 'ryp' ? (
                  <RYPBadge size="micro" style={{ padding: 0 }} showWhiteBorder />
                ) : (
                  <OFPBadge size="micro" style={{ padding: 0 }} showWhiteBorder />
                )}
              </Box>
            )}
          </Box>

          <Box className={classes.mockupContent}>
            <Box className={classes.mockupTitleBar} />

            <Flex className={classes.mockupMetadataContainer}>
              <Box className={classes.mockupMetadataBar} />
              <Box className={classes.mockupMetadataBar} />
              <Box className={classes.mockupMetadataBar} />
            </Flex>
          </Box>
        </Group>
      </Group>
    </Card>
  );

  return (
    <Box className={classes.welcomeHeaderContainer}>
      <Box className={classes.badgeSection}>
        <Flex align="center" justify="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={type === 'combo' ? 38 : theme.other.spacing._16}>
          {renderHeaderBadges()}
        </Flex>
      </Box>

      {!isCardsHidden && (
        <Box className={classes.cardSection}>
          <Box className={classes.topCard}>{renderCardMockup(getCardBadgeType(0))}</Box>

          <Box className={classes.middleCard}>{renderCardMockup(getCardBadgeType(1))}</Box>

          <Box className={classes.bottomCard}>{renderCardMockup(getCardBadgeType(2))}</Box>
        </Box>
      )}
    </Box>
  );
}

export default WelcomeModalHeader;
